import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../services/axios';
import axiosNFE from '../../services/axios_nfe';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  Divider,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material';

// Bibliotecas de PDF e Canvas
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';



function PedidoMobilePage() {
  const { idPedidoMobile } = useParams();
  const [pedido, setPedido] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const logoIntegra = require('../../assets/logos/integra.jpg');
  let logoEmpresa;
  try {
    logoEmpresa = pedido?.logo
      ? require(`../../assets/logos/${pedido.logo}`)
      : require('../../assets/logos/default.png');
  } catch (e) {
    console.error("Erro ao carregar logo:", e);
    logoEmpresa = require('../../assets/logos/default.png');
  }

    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;


  useEffect(() => {
    const fetchPedido = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `/api/imprimir/retornaPedidoMobile/${idPedidoMobile}`
        );
        setPedido(response.data);
      } catch (err) {
        setError('Ocorreu um erro ao buscar o pedido.');
      } finally {
        setLoading(false);
      }
    };

    fetchPedido();
  }, [idPedidoMobile]);

  // -------------------------------------------------------------------
  // FUNÇÃO PARA GERAR PDF COM html2canvas + jsPDF (sem Capacitor)
  // -------------------------------------------------------------------
  const handleGeneratePDF = async () => {
    // Encontramos o container que será impresso
    const input = document.querySelector('.printable-container');
    if (!input) {
      alert('Não foi possível encontrar o container para gerar PDF.');
      return;
    }

    // Pequeno delay para garantir que tudo esteja renderizado
    await new Promise((resolve) => setTimeout(resolve, 300));

    try {
      // Cria o canvas a partir do container
      // A opção scale: 2 aumenta a qualidade do "print"
      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
      });

      // Converte o canvas em imagem base64
      const imgData = canvas.toDataURL('image/png');

      // Cria o PDF em modo retrato (p), mm e A4
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      // Ajusta a altura da imagem proporcional à largura do PDF
      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pdfWidth;
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      // Adiciona a imagem na primeira página
      let position = 0;
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);

      // Se a imagem for maior que uma página, adiciona páginas extras
      let heightLeft = imgHeight - pdfHeight;
      while (heightLeft > 0) {
        position = -pdfHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      // Faz download do PDF diretamente no navegador
      pdf.save('pedido.pdf');
    } catch (err) {
      console.error('Erro ao gerar PDF:', err);
      alert('Ocorreu um erro ao gerar o PDF. Tente novamente.');
    }
  };
  // -------------------------------------------------------------------

  const handleGerarDANFE = async () => {
    if (!pedido) return;
    const id = pedido.nf;
    const matriz = pedido.empresa;
    setLoading(true);
    axiosNFE
      .get(`/api/sicweb/danfExterno?idNota=${id}&idMatriz=${matriz}`, {
        responseType: 'arraybuffer'
      })
      .then(response => {
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      })
      .catch(error => {
        console.error('Erro ao gerar DANFE:', error);
      })
      .finally(() => setLoading(false));
  };

  const handleExportarXML = () => {
    if (!pedido) return;
    const id = pedido.nf;
    const matriz = pedido.empresa;
    setLoading(true);
    axiosNFE
      .get(`/api/sicweb/msgXMLExterno?idNota=${id}&idMatriz=${matriz}`)
      .then(response => {
        const blob = new Blob([response.data], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        window.open(url);
      })
      .catch(error => {
        console.error('Erro ao exportar XML:', error);
      })
      .finally(() => setLoading(false));
  };

  // Renderização condicional (loading, erro, etc.)
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Typography variant="h6" color="error" gutterBottom>
          Seu pedido ainda não está disponível.
        </Typography>
        <Typography variant="body1">
          Pedido ainda não foi enviado ao sistema!!
          Por favor, aguarde e tente novamente mais tarde.
        </Typography>
      </Box>
    );
  }

  if (!pedido) {
    return null; // Não renderiza nada enquanto não tiver o objeto pedido
  }

  // Exemplo de cálculo de total
  const totalQuantidade = pedido.itens?.reduce(
    (acc, item) => acc + (item.quantidade || 0),
    0
  );
  const totalValor = pedido.itens?.reduce(
    (acc, item) => acc + (item.valorTotal || 0),
    0
  );

  // Retorna o bloco de botões, dependendo do statusSefaz
  const renderPrintButtons = () => {
    if (pedido.statusSefaz === 'AUTORIZADA') {
      return (
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportarXML}
            sx={{ mr: 1, mb: 1 }}
          >
            Imprimir XML
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGerarDANFE}
            sx={{ mr: 1, mb: 1 }}
          >
            Imprimir DANFE
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGeneratePDF}
            sx={{ mr: 1, mb: 1 }}
          >
            Imprimir Pedido
          </Button>
        </Box>
      );
    } else {
      return (
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleGeneratePDF}
            sx={{ mr: 1, mb: 1 }}
          >
            Imprimir Pedido
          </Button>
        </Box>
      );
    }
  };

  return (
    <Box
      minHeight="100vh"
      display="flex"
      flexDirection="column"
      bgcolor="#f4f4f4"
    >
      {/* ------------------- CABEÇALHO COM LOGOS ------------------- */}
      <Box
        p={2}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        bgcolor="white"
        boxShadow={1}
      >
        <Box>
          {/* Logo da empresa (ajuste caminho/conteúdo conforme seu projeto) */}
          <img
            src={logoEmpresa}
            alt="Logo da Empresa"
            style={{ maxHeight: 60, objectFit: 'contain' }}
          />
        </Box>
        <Box textAlign="right">
          <Typography variant="caption" color="textSecondary">
            Desenvolvido por:
          </Typography>
          <Box>
            {/* Logo da Integra */}
            <img
              src={logoIntegra}
              alt="Logo da Integra"
              style={{ maxHeight: 80, objectFit: 'contain' }}
            />
          </Box>
        </Box>
      </Box>

      {/* 
        CONTEÚDO PRINCIPAL
        Coloque a classe "printable-container" pois é aqui que o html2canvas 
        buscará o conteúdo para gerar a imagem e, depois, o PDF.
      */}
      <Box
        flex="1"
        p={2}
        sx={{ overflowY: 'auto' }}
        className="printable-container"
      >
        <Paper elevation={3} sx={{ p: 2 }}>
          <Typography variant="h5" gutterBottom>
            Pedido
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {/* Informações do Pedido */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="body2" gutterBottom>
                Número do Pedido: {pedido.numeroPedido}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Cliente: {pedido.nomeCliente}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Status: {pedido.status}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Status Sefaz: {pedido.statusSefaz || 'N/A'}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Observações: {pedido.obsPedido || '-'}
              </Typography>
            </CardContent>
          </Card>

          {/* Tabela de itens */}
          <Typography variant="h6" gutterBottom>
            Itens do Pedido:
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell align="right">Quantidade</TableCell>
                  <TableCell align="right">Vlr. Unitário</TableCell>
                  <TableCell align="right">Vlr. Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pedido.itens?.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.nomeProduto}</TableCell>
                    <TableCell align="right">{item.quantidade}</TableCell>
                    <TableCell align="right">
                      R$ {item.valorUnitario?.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      R$ {item.valorTotal?.toFixed(2)}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Totalizadores */}
                <TableRow>
                  <TableCell>
                    <strong>TOTAL</strong>
                  </TableCell>
                  <TableCell align="right">
                    <strong>{totalQuantidade}</strong>
                  </TableCell>
                  <TableCell align="right">-</TableCell>
                  <TableCell align="right">
                    <strong>R$ {totalValor?.toFixed(2)}</strong>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* RODAPÉ COM BOTÕES */}
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          backgroundColor: 'white',
          borderTop: '1px solid #ccc',
          textAlign: 'center',
          p: 2
        }}
      >
        {renderPrintButtons()}

        <Typography variant="body2" color="textSecondary" gutterBottom>
          Precisa de uma solução como essa?
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Fale conosco: <strong>(43) 3343-3315</strong>
        </Typography>
      </Box>
    </Box>
  );
}

export default PedidoMobilePage;
